<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { clampCropOffset, getCropSourceRect, getOutputFileName, type CropOffsetBounds } from './image-crop';

const props = withDefaults(defineProps<{
  open: boolean;
  file: File | null;
  aspectRatio: number;
  outputWidth: number;
  outputHeight: number;
  outputMimeType: 'image/jpeg' | 'image/png';
}>(), { file: null });

const emit = defineEmits<{
  cancel: [];
  confirm: [payload: { blob: Blob; filename: string }];
  error: [message: string];
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
const zoom = ref(1);
const offset = ref({ x: 0, y: 0 });
const ready = ref(false);
const image = ref<HTMLImageElement | null>(null);
const dragging = ref(false);
let dragStart = { x: 0, y: 0 };
let offsetStart = { x: 0, y: 0 };
let objectUrl: string | undefined;

function canvasSize() {
  const width = 480;
  return { width, height: Math.max(1, Math.round(width / props.aspectRatio)) };
}

function getLayout(): { scale: number; imageWidth: number; imageHeight: number; bounds: CropOffsetBounds } | null {
  const currentImage = image.value;
  if (!currentImage) return null;
  const size = canvasSize();
  const scale = Math.max(size.width / currentImage.naturalWidth, size.height / currentImage.naturalHeight) * zoom.value;
  const imageWidth = currentImage.naturalWidth * scale;
  const imageHeight = currentImage.naturalHeight * scale;
  return {
    scale,
    imageWidth,
    imageHeight,
    bounds: {
      minX: (size.width - imageWidth) / 2,
      maxX: (imageWidth - size.width) / 2,
      minY: (size.height - imageHeight) / 2,
      maxY: (imageHeight - size.height) / 2,
    },
  };
}

function renderPreview() {
  const currentCanvas = canvas.value;
  const currentImage = image.value;
  const layout = getLayout();
  if (!currentCanvas || !currentImage || !layout) return;
  const size = canvasSize();
  currentCanvas.width = size.width;
  currentCanvas.height = size.height;
  const ctx = currentCanvas.getContext('2d');
  if (!ctx) return;
  offset.value = clampCropOffset(offset.value.x, offset.value.y, layout.bounds);
  ctx.clearRect(0, 0, size.width, size.height);
  const x = (size.width - layout.imageWidth) / 2 + offset.value.x;
  const y = (size.height - layout.imageHeight) / 2 + offset.value.y;
  ctx.drawImage(currentImage, x, y, layout.imageWidth, layout.imageHeight);
}

function loadFile(file: File) {
  ready.value = false;
  image.value = null;
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrl = URL.createObjectURL(file);
  const nextImage = new Image();
  nextImage.onload = () => {
    image.value = nextImage;
    zoom.value = 1;
    offset.value = { x: 0, y: 0 };
    ready.value = true;
    void nextTick(renderPreview);
  };
  nextImage.onerror = () => emit('error', '图片处理失败，请重新选择图片或稍后重试');
  nextImage.src = objectUrl;
}

function cleanup() {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrl = undefined;
  image.value = null;
  ready.value = false;
}

function onPointerDown(event: PointerEvent) {
  if (!ready.value) return;
  dragging.value = true;
  dragStart = { x: event.clientX, y: event.clientY };
  offsetStart = { ...offset.value };
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
}

function onPointerMove(event: PointerEvent) {
  if (!dragging.value) return;
  const layout = getLayout();
  if (!layout) return;
  offset.value = clampCropOffset(offsetStart.x + event.clientX - dragStart.x, offsetStart.y + event.clientY - dragStart.y, layout.bounds);
  renderPreview();
}

function onPointerUp() {
  dragging.value = false;
}

function reset() {
  zoom.value = 1;
  offset.value = { x: 0, y: 0 };
  renderPreview();
}

function confirmCrop() {
  const currentImage = image.value;
  const layout = getLayout();
  const currentFile = props.file;
  if (!currentImage || !layout || !currentFile) return;
  const size = canvasSize();
  const source = getCropSourceRect({
    imageWidth: currentImage.naturalWidth,
    imageHeight: currentImage.naturalHeight,
    canvasWidth: size.width,
    canvasHeight: size.height,
    zoom: zoom.value,
    offsetX: offset.value.x,
    offsetY: offset.value.y,
  });
  const output = document.createElement('canvas');
  output.width = props.outputWidth;
  output.height = props.outputHeight;
  const ctx = output.getContext('2d');
  if (!ctx) {
    emit('error', '图片处理失败，请重新选择图片或稍后重试');
    return;
  }
  ctx.drawImage(currentImage, source.x, source.y, source.width, source.height, 0, 0, output.width, output.height);
  output.toBlob((blob) => {
    if (!blob) {
      emit('error', '图片处理失败，请重新选择图片或稍后重试');
      return;
    }
    emit('confirm', { blob, filename: getOutputFileName(currentFile.name, props.outputMimeType) });
  }, props.outputMimeType, props.outputMimeType === 'image/jpeg' ? 0.9 : undefined);
}

function cancel() {
  cleanup();
  emit('cancel');
}

watch(() => [props.open, props.file, props.aspectRatio, props.outputWidth, props.outputHeight, props.outputMimeType] as const, ([open, file]) => {
  if (open && file) loadFile(file);
  if (!open) cleanup();
}, { immediate: true });
</script>

<template>
  <div v-if="open" class="modal-backdrop crop-backdrop" @click.self="cancel">
    <form class="crop-modal" @submit.prevent="confirmCrop">
      <div class="drawer-heading"><div><span class="eyebrow">IMAGE CROP</span><h2>调整图片显示效果</h2></div><button type="button" class="drawer-close" aria-label="关闭" @click="cancel">×</button></div>
      <p class="crop-hint">拖动图片调整位置，缩放后确认；小程序会按此裁切结果展示。</p>
      <div class="crop-stage" :style="{ aspectRatio: String(aspectRatio) }"><canvas ref="canvas" @pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="onPointerUp" @pointercancel="onPointerUp" /></div>
      <label class="crop-zoom">缩放<input v-model.number="zoom" type="range" min="1" max="3" step="0.01" @input="renderPreview" /></label>
      <div class="modal-actions"><button type="button" class="outline-button" @click="reset">重置</button><button type="button" class="outline-button" @click="cancel">取消</button><button class="primary-button" :disabled="!ready">确认裁切</button></div>
    </form>
  </div>
</template>
